import {
  MediaBarSubType,
  MediaCellarSubType,
  type MediaData,
  MediaType,
  type MediaUploadDetails,
  REF_MATERIALS_PATH_PREFIX
} from '../models';

/**
 * Validates if passed in media is a video type
 * @param media
 */
export const isVideo = (media: MediaData): boolean =>
  !!media.fileType?.startsWith('video/') || !!media.contentUrl?.endsWith('.mp4');

/**
 * Extracts a frame from a video and returns it's url as an image
 * Default frame (1s)
 * @param url
 * @param atSeconds
 */
export const extractVideoFrame = (url: string, atSeconds = 1): Promise<string> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    // Attempt to enable CORS so we can paint to canvas
    video.crossOrigin = 'anonymous';
    video.preload = 'auto';
    video.src = url;

    const onError = () => reject(new Error('Failed to load video for thumbnail extraction'));
    video.addEventListener('error', onError, { once: true });

    video.addEventListener(
      'loadedmetadata',
      () => {
        // Choose a safe timestamp within duration
        const target =
          Number.isFinite(video.duration) && video.duration > 0
            ? Math.min(Math.max(atSeconds, 0.1), Math.max(video.duration - 0.1, 0.1))
            : atSeconds;
        const seek = () => {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth || 1280;
          canvas.height = video.videoHeight || 720;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          try {
            const dataUrl = canvas.toDataURL('image/jpeg');
            resolve(dataUrl);
          } catch (e) {
            reject(e);
          }
        };

        if (Math.abs(video.currentTime - target) < 0.05) {
          seek();
        } else {
          const onSeeked = () => {
            video.removeEventListener('seeked', onSeeked);
            seek();
          };
          video.addEventListener('seeked', onSeeked);
          try {
            video.currentTime = target;
          } catch (_e) {
            // Fallback: if seeking fails, try playing briefly then pause
            const onTimeUpdate = () => {
              if (video.currentTime >= 0.5) {
                video.pause();
                video.removeEventListener('timeupdate', onTimeUpdate);
                seek();
              }
            };
            video.addEventListener('timeupdate', onTimeUpdate);
            video.play().catch(() => onError());
          }
        }
      },
      { once: true }
    );
  });
};

export const constructS3PrefixPath = (mediaParams: MediaUploadDetails): string => {
  const { type, subType } = mediaParams;

  switch (type) {
    case MediaType.VISIT:
      return `visits/${mediaParams.visitId}/signature`;
    case MediaType.COMMENT:
      return `visits/${mediaParams.visitId}/comments/${mediaParams.commentId}`;
    case MediaType.BAR:
      switch (subType) {
        case MediaBarSubType.GLASSWARE:
          return `visits/${mediaParams.visitId}/visit-bars/${mediaParams.visitBarId}/glassware`;
        case MediaBarSubType.NOZZLES:
          return `visits/${mediaParams.visitId}/visit-bars/${mediaParams.visitBarId}/nozzles`;
        case MediaBarSubType.PERFECT_PINT:
          return `visits/${mediaParams.visitId}/visit-bars/${mediaParams.visitBarId}/perfect-pint`;
        case MediaBarSubType.LINES_PH:
          return `visits/${mediaParams.visitId}/visit-bars/${mediaParams.visitBarId}/safety-check`;
        case MediaBarSubType.TAP_HYGIENE:
          return `visits/${mediaParams.visitId}/visit-bars/${mediaParams.visitBarId}/tap-hygiene`;
        default:
          return '';
      }
    case MediaType.CELLAR:
      switch (subType) {
        case MediaCellarSubType.FOB_INTERNAL_HYGIENE:
          return `visits/${mediaParams.visitId}/visit-cellars/${mediaParams.visitCellarId}/fob-internal-hygiene`;
        case MediaCellarSubType.COUPLER_HYGIENE:
          return `visits/${mediaParams.visitId}/visit-cellars/${mediaParams.visitCellarId}/coupler-hygiene`;
        case MediaCellarSubType.SOCKET_HYGIENE:
          return `visits/${mediaParams.visitId}/visit-cellars/${mediaParams.visitCellarId}/socket-hygiene`;
        case MediaCellarSubType.BACK_BOARD_HYGIENE:
          return `visits/${mediaParams.visitId}/visit-cellars/${mediaParams.visitCellarId}/back-board-hygiene`;
        case MediaCellarSubType.PYTHON:
          return `visits/${mediaParams.visitId}/visit-cellars/${mediaParams.visitCellarId}/python`;
        default:
          return '';
      }
    case MediaType.TAP:
      return `visits/${mediaParams.visitId}/visit-taps/${mediaParams.visitTapId}`;
    case MediaType.FOB:
      return `visits/${mediaParams.visitId}/visit-fobs/${mediaParams.visitFobId}`;
    case MediaType.REMOTE_COOLER:
      return `visits/${mediaParams.visitId}/visit-remote-coolers/${mediaParams.visitRemoteCoolerId}`;
    case MediaType.GLASS_WASHER:
      return `visits/${mediaParams.visitId}/visit-glass-washers/${mediaParams.visitGlassWasherId}`;
    case MediaType.AMS_PART:
      return `visits/${mediaParams.visitId}/visit-ams-parts/${mediaParams.visitAmsPartId}`;
    case MediaType.FAULT_INTERACTION:
      return `visits/${mediaParams.visitId}/visit-fault-interactions/${mediaParams.visitFaultInteractionId}`;
    case MediaType.VENUE:
      return `venues/${mediaParams.venueId}/pss`;
    case MediaType.REFERENCE_MATERIALS:
      return REF_MATERIALS_PATH_PREFIX;
  }
};
